<template>
    <div class="space-y-2">
        <InlineCreateAllocationVariant
            v-if="canCreateVariant"
            :status="inlineCreateStatus"
            @click="createNewInlineVariant"
        />
        <div v-if="!loading && variantsRef.length > 0" class="space-y-2">
            <EventVariantCard
                v-for="variant in variantsRef"
                ref="variantCardCompositionRef"
                :key="variant.id"
                :event-id="eventId"
                :variant="variant"
                :variant-type="variantType"
                :variants-ref="variantsRef"
                :phase-types="eventPhaseTypes"
                :link-to-reference-allocation="{
                    name: 'event-allocations-tab',
                    params: { eventId },
                }"
                :reference-allocation-query-document="getReferenceAllocationQueryDocument(variant)"
                :reference-allocation-link-label="$t('phases-tile-title-reference-allocation')"
                :set-variants-ref="setVariantsRef"
                :set-current-variant-loading="setCurrentVariantLoading"
                :set-current-variant-error="setCurrentVariantError"
                @set-current-variant="(variantId) => onSetCurrentVariant(variantId)"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import InlineCreateAllocationVariant from '@/components/InlineCreateAllocationVariant/InlineCreateAllocationVariant.vue';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import EventVariantCard, { EventVariantCardExpose } from '@/event/components/EventVariantCard.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useEventVariants } from '@/event/composables/useEventVariants';
import {
    AllocationAreaPhaseTileDataDocument,
    AllocationRoomPhaseTileDataDocument,
    GetAllocationUsagesDocument,
} from '@/generated/graphql';
import { VariantCardVariant } from '@/variant/types';
import { useQuery } from '@vue/apollo-composable';
import { computed, Ref, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const props = defineProps<{ eventId: string }>();
const emit = defineEmits<{
    (e: 'handleTabRouteLeave'): void;
}>();
const eventId = computed(() => props.eventId);
const variantType = 'Event';

const variantCardCompositionRef: Ref<EventVariantCardExpose[]> = ref([]);

const { event } = useEventInjection();
const canCreateVariant = computed(() => auth.isAdmin());

const {
    variantsRef,
    loading,
    setCurrentVariantLoading,
    setCurrentVariantError,
    inlineCreateStatus,
    setVariantsRef,
    setCurrentVariant,
    createNewInlineVariant,
} = useEventVariants(event, variantCardCompositionRef);

async function onSetCurrentVariant(variantId: string) {
    setCurrentVariant(variantId);
}

function getReferenceAllocationQueryDocument(variant: VariantCardVariant) {
    if (variant.referenceAllocationAreaId) {
        return AllocationAreaPhaseTileDataDocument;
    }
    return AllocationRoomPhaseTileDataDocument;
}

// as the usages do not change during a session we can use the cache here
const { result: allocationUsages } = useQuery(GetAllocationUsagesDocument, null, {
    fetchPolicy: 'cache-first',
});

const eventPhaseTypes = computed(
    () =>
        allocationUsages.value?.allocationUsages?.nodes.map((n) => ({
            ...n,
            color: mapToPhaseColor(n.type),
        })) ?? [],
);

onBeforeRouteLeave(() => {
    emit('handleTabRouteLeave');
    return true;
});
</script>
